<template>
  <standard-dialog
    v-model="model"
    :title="$t('tooltip_delivery_settings')"
    :subtitle="firm && firm.name ? firm.name + ' | ID: ' + firm.id : ''"
    icon="mdi-cog-outline"
    :max-width="520"
    :loading="saving"
    :disabled="saving"
    @save="save"
    @close="close"
  >
    <v-row>
      <v-col cols="12" sm="6">
        <v-checkbox
          v-model="link.active_restoran"
          :label="$t('status_restoran')"
          hide-details
          dense
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-checkbox
          v-model="link.active_company"
          :label="$t('status_company')"
          hide-details
          dense
        />
      </v-col>
      <v-col cols="12" sm="6">
        <autocomplete
          v-model="link.paying_type"
          :items="payingTypeOptions"
          item-text="name"
          item-value="val"
          :label="$t('pay_type')"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <text-field
          v-model="link.paying"
          :label="$t('amount')"
          :suffix="paymentCurrency"
        />
      </v-col>
      <v-col cols="12">
        <v-checkbox
          v-model="link.internal"
          :label="$t('internal')"
          hide-details
          dense
        />
      </v-col>
    </v-row>

    <global-confirm-box ref="confirmBox" />
  </standard-dialog>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import companyService from "@/services/company";
import {
  hasCurrencyMismatch,
  getRestaurantCurrency,
  getDeliveryCompanyCurrency,
} from "@/utils/currency";

// Restaurant-facing counterpart to RestaurantSettingsDialog.vue. That dialog
// lists every restaurant cooperating with a delivery firm (right for admin,
// who legitimately manages the whole network) - a plain restaurant account
// only ever needs its OWN cooperation row, so this component never receives
// the firm's employee list or other restaurants' data as props in the first
// place, rather than fetching and then hiding them in the UI.
export default {
  name: "RestaurantOwnDeliverySettingsDialog",
  components: { StandardDialog },
  mixins: [hasModel, hasSnackbar],
  props: {
    firm: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      saving: false,
      link: this.buildLink(),
    };
  },
  computed: {
    payingTypeOptions() {
      return [
        { name: this.$t("choose"), val: 0 },
        { name: this.$t("by_month"), val: 1 },
        { name: this.$t("by_percent"), val: 2 },
        { name: this.$t("by_meal"), val: 3 },
      ];
    },
    // "By percent" isn't a currency amount - only by_month/by_meal are. The
    // delivery firm's own currency is what it pays/charges, falling back to
    // the restaurant's currency for the rare firm without one set.
    paymentCurrency() {
      if (this.link.paying_type === 2) return "%";
      return getDeliveryCompanyCurrency(this.firm) || this.restaurantCurrency;
    },
  },
  watch: {
    model(isOpen) {
      if (isOpen) this.link = this.buildLink();
    },
  },
  methods: {
    // The logged-in restaurant's existing link row for this firm, if one
    // already exists - otherwise a fresh (unlinked) row so Save creates one.
    buildLink() {
      const restaurantId = this.$store.state.restaurant && this.$store.state.restaurant.id;
      const links = (this.firm && this.firm.restaurant_delivery_company) || [];
      const existing = links.find((row) => row.restaurant_id === restaurantId);
      return {
        id: restaurantId,
        restaurant_id: existing ? restaurantId : null,
        active_restoran: existing ? !!existing.active_restoran : false,
        active_company: existing ? !!existing.active_company : false,
        paying_type: existing ? existing.paying_type : null,
        paying: existing ? existing.paying : null,
        internal: existing ? !!existing.internal : false,
      };
    },
    async confirmCurrencyMismatch() {
      const restaurant = this.$store.state.restaurant;
      if (!hasCurrencyMismatch(restaurant, this.firm)) return true;
      try {
        await this.$refs.confirmBox.open(
          this.$t("currency_mismatch_title"),
          this.$t("currency_mismatch_warning", {
            restaurant: (restaurant && restaurant.name) || "",
            restaurantCurrency: getRestaurantCurrency(restaurant),
            companyCurrency: getDeliveryCompanyCurrency(this.firm),
          }),
          { color: "warning", width: 460 }
        );
        return true;
      } catch (e) {
        return false;
      }
    },
    async save() {
      if (!(await this.confirmCurrencyMismatch())) return;
      this.saving = true;
      try {
        this.link.restaurant_id = this.link.id;
        // Only the firm's id is needed to attach the link server-side - never
        // send the full firm object (it carries every employee's PII/login
        // tokens and every other restaurant's contact info, see BUG 15/17).
        await companyService.storeDeliveryCompanyRestaurants({
          restorani: [this.link],
          delivery_company: { id: this.firm.id },
          restoran_id: this.link.id,
        });
        this.showSuccessSnackbar(this.$t("firm_settings_saved"));
        this.$emit("saved");
        this.close();
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_saving_firm_settings"));
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.model = false;
      this.$emit("close");
    },
  },
};
</script>
